import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n, catLabel } from "../lib/i18n";
import { StorefrontHeader } from "../components/StorefrontHeader";
import { PromoBanners } from "../components/PromoBanners";
import { OnlineCart } from "../components/OnlineCart";
import { useReorder } from "../lib/useReorder";
import { useStore } from "../lib/store";
import { useCart } from "../lib/cart";
import { CategoryGrid } from "../components/CategoryGrid";
import { ProductRow } from "../components/ProductRow";
import { ProductCard } from "../components/ProductCard";
import { FlashSale } from "../components/FlashSale";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { PosterSlot } from "../components/PosterSlot";
import { ProductSearch, matchesQuery } from "../components/ProductSearch";
import { uniqueCats, sameCat } from "../lib/cats";

const Catalog = ({ products, onPick, cat, setCat, q }) => {
  const { t, lang } = useI18n();
  const cats = ["__all__", ...uniqueCats(products.map((p) => p.category))];
  const list = products.filter(
    (p) => (cat === "__all__" || sameCat(p.category, cat)) && matchesQuery(p, q)
  );
  return (
    <div className="space-y-4 scroll-mt-4" id="catalog-section" data-testid="shop-catalog-section">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`shop-category-${c === "__all__" ? "all" : c}`}
            onClick={() => setCat(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
              cat === c
                ? "bg-gray-900 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {c === "__all__" ? t("all") : catLabel(c, lang)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-4">
        {list.length === 0 && (
          <p className="col-span-full text-sm text-muted-foreground py-6" data-testid="shop-empty">
            {t("no_data")}
          </p>
        )}
        {list.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            onPick={onPick}
            testid={`shop-product-${p.id}`}
          />
        ))}
      </div>
    </div>
  );
};

export default function TokoOnline() {
  const { t } = useI18n();
  const { categoryOrder, categoryIcons, featuredIds } = useStore();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [items, setItems] = useCart("grocery");
  const addedRef = useRef(null);
  const cat = params.get("cat") || "__all__";
  const setCat = (c) => setParams(c === "__all__" ? {} : { cat: c });

  const load = useCallback(
    () =>
      api
        .get("/products", { params: { kind: "grocery" } })
        .then(({ data }) => setProducts(data)),
    []
  );
  useEffect(() => {
    load();
  }, [load]);
  useReorder("grocery", products, setItems);

  useEffect(() => {
    const addId = params.get("add");
    if (!addId || !products.length) return;
    if (addedRef.current === addId) return;
    addedRef.current = addId;
    const p = products.find((x) => x.id === addId);
    if (p) {
      setItems((prev) =>
        prev.find((i) => i.product_id === p.id)
          ? prev.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i))
          : [...prev, { product_id: p.id, name: p.name, price: p.price, qty: 1, unit: p.unit, category: p.category }]
      );
      toast.success(`${p.name} ${t("added_to_cart")}`);
    }
    setParams({}, { replace: true });
  }, [params, products, setParams, setItems, t]);

  const featured = useMemo(
    () => featuredIds.map((id) => products.find((p) => p.id === id)).filter(Boolean),
    [featuredIds, products]
  );
  const newest = useMemo(
    () =>
      [...products]
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 10),
    [products]
  );

  const results = useMemo(
    () =>
      products.filter(
        (p) => matchesQuery(p, q) && (cat === "__all__" || sameCat(p.category, cat))
      ),
    [products, q, cat]
  );

  const onSearch = (v) => {
    setQ(v);
    if (v.trim().length === 1)
      document.getElementById("catalog-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const pick = (p) =>
    setItems((prev) => {
      const f = prev.find((i) => i.product_id === p.id);
      if (f)
        return prev.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [
        ...prev,
        { product_id: p.id, name: p.name, price: p.price, qty: 1, unit: p.unit, category: p.category },
      ];
    });

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      <StorefrontHeader active="toko" />
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 grid lg:grid-cols-3 xl:grid-cols-4 gap-8 min-w-0">
        <div className="lg:col-span-2 xl:col-span-3 space-y-6 min-w-0">
          <PromoBanners channel="grocery" />
          <ProductSearch
            value={q}
            onChange={onSearch}
            count={results.length}
            prefix="shop"
          />
          <FlashSale />
          <div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold">
              {t("toko_title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t("toko_sub")}</p>
          </div>
          {!q.trim() && (
            <>
              <CategoryGrid
                products={products}
                order={categoryOrder}
                icons={categoryIcons}
                cat={cat}
                setCat={setCat}
                prefix="shop"
              />
              <ProductRow
                title={t("featured_products")}
                products={featured}
                onPick={pick}
                prefix="shop-featured"
              />
              <ProductRow
                title={t("new_arrivals")}
                products={newest}
                onPick={pick}
                prefix="shop-new"
              />
            </>
          )}
          <PosterSlot slot="catalog_mid" channel="grocery" />
          <Catalog products={products} onPick={pick} cat={cat} setCat={setCat} q={q} />
          <PosterSlot slot="footer" channel="grocery" />
        </div>
        <div className="lg:sticky lg:top-6 h-fit space-y-6">
          <OnlineCart channel="grocery" items={items} setItems={setItems} onDone={load} />
          <PosterSlot slot="sidebar" channel="grocery" />
        </div>
      </div>
      <WhatsAppFloat />
    </div>
  );
}
